import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { api } from './api';
import { amount, decimalInput, plain, QTY_DECIMALS } from './ui';

interface Props {
  order: any;
  warehouses: { value: string; label: string }[];
  onClose: () => void;
  onDone: () => void;
}

interface Row {
  id: string;
  include: boolean;
  arrived: string;
  accepted: string;
  /** Once the accepted amount is edited by hand it stops following the arrived amount. */
  acceptedTouched: boolean;
  final: boolean;
  reason: string;
}

/**
 * Records what actually arrived for every pending shipment of the order in one go. Each line
 * starts as "all arrived, all accepted"; only exceptions need editing.
 */
export default function ReceiveDialog(p: Props) {
  const pending = p.order.shipments.filter((s: any) => !s.finalized);
  const skuOf = (s: any) =>
    p.order.lines.find((l: any) => l.id === s.trade_line_id)?.sku_snapshot ?? {};
  const left = (s: any) => plain(Number(s.quantity) - Number(s.arrived), QTY_DECIMALS);
  const [rows, setRows] = useState<Row[]>(() =>
    pending.map((s: any) => ({
      id: s.id,
      include: true,
      arrived: left(s),
      accepted: left(s),
      acceptedTouched: false,
      final: true,
      reason: '',
    })),
  );
  const [warehouseId, setWarehouseId] = useState(p.warehouses[0]?.value ?? '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => crypto.randomUUID());

  const update = (id: string, patch: Partial<Row>) =>
    setRows((all) => all.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const shipmentOf = (id: string) => pending.find((s: any) => s.id === id);
  // A line needs a reason when it closes short of what was shipped or has rejects.
  const needsReason = (r: Row) => {
    const s = shipmentOf(r.id);
    return (
      Number(r.accepted) < Number(r.arrived) || (r.final && Number(r.accepted) < Number(left(s)))
    );
  };
  const chosen = rows.filter((r) => r.include);

  async function submit() {
    if (busy) return;
    if (!chosen.length) return setError('请至少勾选一批到货');
    for (const r of chosen) {
      const name = skuOf(shipmentOf(r.id)).name;
      if (r.arrived === '' || r.accepted === '') return setError(`${name}：请填写实到和合格数量`);
      if (Number(r.accepted) > Number(r.arrived))
        return setError(`${name}：合格数量不能大于实到数量`);
      if (!r.final && !(Number(r.arrived) > 0)) return setError(`${name}：分次收货数量必须大于零`);
      if (needsReason(r) && r.reason.trim().length < 2)
        return setError(`${name}：有少货或不合格，请填写原因`);
    }
    setBusy(true);
    setError('');
    try {
      await api(`/orders/${p.order.id}/receive`, {
        warehouseId,
        key,
        lines: chosen.map((r) => ({
          shipmentLineId: r.id,
          arrived: r.arrived,
          accepted: r.accepted,
          final: r.final,
          reason: r.reason.trim(),
        })),
      });
      p.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && p.onClose()}>
      <DialogTitle sx={{ pb: 1 }}>
        登记实际收货验收
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          默认按发货数量全部到货、全部合格；有少货或不合格时改成实际数量并写明原因。实到但不合格的进入待检。
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="入库仓库"
            value={warehouseId}
            disabled={busy}
            onChange={(e) => setWarehouseId(e.target.value)}
            sx={{ maxWidth: 320 }}
          >
            {p.warehouses.map((w) => (
              <MenuItem key={w.value} value={w.value}>
                {w.label}
              </MenuItem>
            ))}
          </TextField>
          {rows.map((r) => {
            const s = shipmentOf(r.id);
            const sku = skuOf(s);
            const unit = <InputAdornment position="end">{sku.unit}</InputAdornment>;
            const short = r.include && needsReason(r);
            return (
              <Box
                key={r.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: short ? '#f0d9b5' : '#e7edef',
                  bgcolor: r.include ? (short ? '#fffbf3' : '#fff') : '#f6f8f8',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={r.include}
                    disabled={busy}
                    onChange={(e) => update(r.id, { include: e.target.checked })}
                    sx={{ ml: -1 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 650 }} noWrap>
                      {sku.name}
                      <Typography
                        component="span"
                        color="text.secondary"
                        sx={{ fontSize: 12, ml: 1 }}
                      >
                        {sku.spec}
                      </Typography>
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                      {s.number} · 发货 {amount(s.quantity)} {sku.unit}
                      {Number(s.arrived) > 0 && ` · 已实到 ${amount(s.arrived)}`}
                    </Typography>
                  </Box>
                  {!r.include && (
                    <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                      本次不登记
                    </Typography>
                  )}
                </Box>
                {r.include && (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1.2fr' },
                        gap: 1.5,
                        mt: 1.5,
                        alignItems: 'center',
                      }}
                    >
                      <TextField
                        size="small"
                        label="实到"
                        value={r.arrived}
                        disabled={busy}
                        onChange={(e) => {
                          const arrived = decimalInput(e.target.value, QTY_DECIMALS);
                          update(
                            r.id,
                            r.acceptedTouched ? { arrived } : { arrived, accepted: arrived },
                          );
                        }}
                        slotProps={{ input: { endAdornment: unit } }}
                      />
                      <TextField
                        size="small"
                        label="其中合格"
                        value={r.accepted}
                        disabled={busy}
                        onChange={(e) =>
                          update(r.id, {
                            accepted: decimalInput(e.target.value, QTY_DECIMALS),
                            acceptedTouched: true,
                          })
                        }
                        slotProps={{ input: { endAdornment: unit } }}
                      />
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={r.final}
                            disabled={busy}
                            onChange={(e) => update(r.id, { final: e.target.checked })}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: 13 }}>
                            本批已收完
                            <Typography
                              component="span"
                              color="text.secondary"
                              sx={{ fontSize: 12, ml: 0.5 }}
                            >
                              {r.final ? '（短少计为差异）' : '（后续还有到货）'}
                            </Typography>
                          </Typography>
                        }
                      />
                    </Box>
                    {short && (
                      <TextField
                        size="small"
                        fullWidth
                        label="差异原因"
                        placeholder="例如：少 1 箱，2 个瓶口变形"
                        value={r.reason}
                        disabled={busy}
                        onChange={(e) => update(r.id, { reason: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ mt: 1.5 }}
                      />
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography color="text.secondary" sx={{ flex: 1, fontSize: 12.5 }}>
          已选 {chosen.length} / {rows.length} 批，一次提交，全部成功或全部不生效。
        </Typography>
        <Button disabled={busy} onClick={p.onClose}>
          取消
        </Button>
        <Button variant="contained" disabled={busy || !chosen.length} onClick={submit}>
          {busy ? '正在提交…' : '确认收货'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
